import React, { useEffect, useState } from 'react';
import axios from 'axios';
import PitchMatchGraph from './PitchMatchGraph';
import LoudnessMatchGraph from './LoudnessMatchGraph';
import MaskingCurveGraph from './MaskingCurveGraph';
import {
  MASKING_LEVELS, normaliseTinnitogram, derivePitchType, deriveLaterality, activeEarForSummary,
  buildClinicalInterpretation, buildRecommendations,
} from './tinnitogramLogic';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BRAND = {
  primary: '#1a56db', primarySoft: '#e6efff',
  tableHead: '#dbeafe', tableAlt: '#f8fafc',
  border: '#cbd5e1', textMuted: '#64748b',
};

const TinnitogramReportView = ({ state, patient, clinic, audiologistName, license, sessionId }) => {
  const s = state;
  const ear = activeEarForSummary(s);
  const pitchMatch = s.pitch[ear];
  const loud = s.loudness[ear];
  const printedOn = new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  const tType = derivePitchType(pitchMatch.match_freq);
  const lat   = deriveLaterality(s.test_ear);
  const clin  = s.clinical_interpretation || buildClinicalInterpretation(s);
  const recs  = s.recommendations || buildRecommendations();

  const InfoCell = ({ label, value }) => (
    <div className="flex text-[9.5px] leading-tight">
      <div className="w-2/5 font-semibold" style={{ color: BRAND.textMuted }}>{label}</div>
      <div className="flex-1" style={{ color: '#0f172a' }}>: {value || '—'}</div>
    </div>
  );

  return (
    <div id="tinnitogram-report-canvas" data-testid="tinnitogram-report-canvas"
      className="tinnitogram-report bg-white text-slate-900"
      style={{ width: '210mm', minHeight: '297mm', padding: '8mm', boxSizing: 'border-box', fontFamily: 'Arial, Helvetica, sans-serif', color: '#0f172a', WebkitPrintColorAdjust: 'exact', printColorAdjust: 'exact' }}
    >
      {/* Letterhead */}
      <div className="flex items-center justify-between border-b pb-1 mb-1.5" style={{ borderColor: BRAND.border }}>
        <div className="flex items-center gap-2">
          {clinic?.logo_base64 && <img src={clinic.logo_base64} alt="logo" style={{ width: 52, height: 52, objectFit: 'contain' }} />}
          <div>
            <div className="text-[16px] font-black" style={{ color: BRAND.primary, letterSpacing: 0.5 }}>{clinic?.name || 'audinexa'}</div>
            {clinic?.tagline && <div className="text-[9px] italic" style={{ color: BRAND.textMuted }}>{clinic.tagline}</div>}
          </div>
        </div>
        <div className="text-center">
          <div className="text-[10px] font-bold">Smarter Audiology.</div>
          <div className="text-[10px] font-bold">Healthier Hearing Futures.</div>
        </div>
        <div className="text-right" style={{ color: BRAND.primary, fontFamily: 'cursive' }}>
          <div className="text-[13px] italic font-semibold">Better Care</div>
          <div className="text-[13px] italic font-semibold">Brighter Lives</div>
        </div>
      </div>

      {/* Title band */}
      <div className="text-center py-1.5 rounded" style={{ backgroundColor: BRAND.primarySoft }} data-testid="tin-report-title-band">
        <div className="text-[16px] font-black uppercase" style={{ color: BRAND.primary, letterSpacing: 1 }}>Tinnitogram Report</div>
        <div className="text-[10px] font-bold tracking-widest" style={{ color: BRAND.primary }}>Assessment of Tinnitus Characteristics</div>
      </div>

      {/* Info grid */}
      <div className="grid grid-cols-3 gap-2 mt-2 border rounded px-2 py-1.5" style={{ borderColor: BRAND.border }} data-testid="tin-report-info">
        <div className="space-y-0.5">
          <InfoCell label="Patient Name" value={patient?.name} />
          <InfoCell label="Patient ID" value={patient?.mrd || patient?.patient_id} />
          <InfoCell label="Age / Gender" value={patient?.age ? `${patient.age} years / ${patient.gender ? patient.gender[0]?.toUpperCase() + patient.gender.slice(1) : ''}` : ''} />
          <InfoCell label="Date of Test" value={printedOn} />
          <InfoCell label="Refer by" value={s.refer_by} />
        </div>
        <div className="space-y-0.5">
          <InfoCell label="Clinic Name" value={clinic?.name} />
          <InfoCell label="Clinician" value={s.clinician || audiologistName} />
          <InfoCell label="Test Environment" value={s.environment} />
          <InfoCell label="Audiometer" value={s.audiometer} />
          <InfoCell label="Transducer" value={s.transducer} />
          <InfoCell label="Test Ear" value={s.test_ear === 'Right' ? 'Right Ear' : s.test_ear === 'Left' ? 'Left Ear' : 'Both Ears'} />
        </div>
        <div className="space-y-0.5">
          <InfoCell label="Procedure" value={s.procedure} />
          <InfoCell label="Test Standard" value={s.test_standards} />
          <InfoCell label="Purpose" value={s.purpose} />
          <InfoCell label="Counseling Given" value={s.counseling_given} />
          <InfoCell label="Remarks" value={s.remarks} />
        </div>
      </div>

      {/* Two-column body: LEFT = pitch + masking; RIGHT = loudness + summary */}
      <div className="grid grid-cols-2 gap-2 mt-2" data-testid="tin-report-body">
        <div className="space-y-2">
          {/* Section 1 */}
          <div className="border rounded" style={{ borderColor: BRAND.border }} data-testid="tin-report-pitch">
            <div className="px-2 py-1 font-bold text-[11px]" style={{ color: BRAND.primary, backgroundColor: BRAND.primarySoft }}>1. Tinnitus Pitch Match</div>
            <div className="px-2 pt-1 pb-2">
              <PitchMatchGraph pitch={s.pitch} readOnly width={330} height={180} />
              <table className="w-full text-[9px] border mt-1" style={{ borderColor: BRAND.border, borderCollapse: 'collapse' }}>
                <thead><tr style={{ backgroundColor: BRAND.tableHead }}>
                  <th className="border px-1 py-0.5" style={{ borderColor: BRAND.border }}>Ear</th>
                  <th className="border px-1 py-0.5" style={{ borderColor: BRAND.border }}>Tinnitus Pitch Match Frequency (Hz)</th>
                  <th className="border px-1 py-0.5" style={{ borderColor: BRAND.border }}>Equivalent Hearing Level (dB HL)</th>
                </tr></thead>
                <tbody>
                  {['right', 'left'].map((e, i) => (
                    <tr key={e} style={{ backgroundColor: i % 2 ? BRAND.tableAlt : '#ffffff' }}>
                      <td className="border px-1 py-0.5" style={{ borderColor: BRAND.border, color: e === 'right' ? '#dc2626' : '#2563eb', fontWeight: 700 }}>{e === 'right' ? 'Right Ear (RE)' : 'Left Ear (LE)'}</td>
                      <td className="border px-1 py-0.5 text-center font-mono" style={{ borderColor: BRAND.border }}>{s.pitch[e].match_freq ? s.pitch[e].match_freq.toLocaleString() : '—'}</td>
                      <td className="border px-1 py-0.5 text-center font-mono" style={{ borderColor: BRAND.border }}>{s.pitch[e].match_hl !== null && s.pitch[e].match_hl !== undefined ? s.pitch[e].match_hl : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          {/* Section 3 */}
          <div className="border rounded" style={{ borderColor: BRAND.border }} data-testid="tin-report-masking">
            <div className="px-2 py-1 font-bold text-[11px]" style={{ color: BRAND.primary, backgroundColor: BRAND.primarySoft }}>3. Masking Curve <span className="font-normal text-[9px]" style={{ color: BRAND.textMuted }}>(Both Ears)</span></div>
            <div className="px-2 pt-1 pb-2">
              <MaskingCurveGraph masking={s.masking} readOnly width={330} height={190} />
            </div>
          </div>
        </div>
        <div className="space-y-2">
          {/* Section 2 */}
          <div className="border rounded" style={{ borderColor: BRAND.border }} data-testid="tin-report-loudness">
            <div className="px-2 py-1 font-bold text-[11px]" style={{ color: BRAND.primary, backgroundColor: BRAND.primarySoft }}>2. Tinnitus Loudness Match</div>
            <div className="px-2 pt-1 pb-2">
              <LoudnessMatchGraph loudness={s.loudness} activeEar={ear} readOnly width={330} height={180} />
              <table className="w-full text-[9px] border mt-1" style={{ borderColor: BRAND.border, borderCollapse: 'collapse' }}>
                <thead><tr style={{ backgroundColor: BRAND.tableHead }}>
                  <th className="border px-1 py-0.5 text-left" style={{ borderColor: BRAND.border }}>Parameter</th>
                  <th className="border px-1 py-0.5" style={{ borderColor: BRAND.border, color: '#dc2626' }}>Right Ear (RE)</th>
                  <th className="border px-1 py-0.5" style={{ borderColor: BRAND.border, color: '#2563eb' }}>Left Ear (LE)</th>
                </tr></thead>
                <tbody>
                  {[
                    ['Tinnitus Pitch Match (Hz)', s.pitch.right.match_freq?.toLocaleString(), s.pitch.left.match_freq?.toLocaleString()],
                    ['Tinnitus Loudness Match (dB SL)', s.loudness.right.loudness_db_sl, s.loudness.left.loudness_db_sl],
                    ['Minimum Masking Level (dB SL)', s.loudness.right.mml_db_sl, s.loudness.left.mml_db_sl],
                    ['Residual Inhibition (RI)',
                      s.loudness.right.ri_present ? `Present${s.loudness.right.ri_minutes ? ` (${s.loudness.right.ri_minutes} min)` : ''}` : 'Absent',
                      s.loudness.left.ri_present ? `Present${s.loudness.left.ri_minutes ? ` (${s.loudness.left.ri_minutes} min)` : ''}` : (s.loudness.left.loudness_db_sl !== null && s.loudness.left.loudness_db_sl !== undefined ? 'Absent' : '—')
                    ],
                  ].map(([lbl, r, l], i) => (
                    <tr key={i} style={{ backgroundColor: i % 2 ? BRAND.tableAlt : '#ffffff' }}>
                      <td className="border px-1 py-0.5 font-semibold" style={{ borderColor: BRAND.border }}>{lbl}</td>
                      <td className="border px-1 py-0.5 text-center" style={{ borderColor: BRAND.border }}>{r ?? '—'}</td>
                      <td className="border px-1 py-0.5 text-center" style={{ borderColor: BRAND.border }}>{l ?? '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          {/* Summary */}
          <div className="border rounded" style={{ borderColor: BRAND.border }} data-testid="tin-report-summary">
            <div className="px-2 py-1 font-bold text-[11px]" style={{ color: BRAND.primary, backgroundColor: BRAND.primarySoft }}>Summary of Results</div>
            <table className="w-full text-[9.5px]" style={{ borderCollapse: 'collapse' }}>
              <tbody>
                {[
                  ['Tinnitus Type (based on pitch)', tType || '—'],
                  ['Tinnitus Pitch Match', pitchMatch.match_freq ? `${pitchMatch.match_freq.toLocaleString()} Hz (${ear === 'right' ? 'Right Ear' : 'Left Ear'})` : '—'],
                  ['Tinnitus Loudness Match', loud.loudness_db_sl !== null && loud.loudness_db_sl !== undefined ? `${loud.loudness_db_sl} dB SL` : '—'],
                  ['Minimum Masking Level (MML)', loud.mml_db_sl !== null && loud.mml_db_sl !== undefined ? `${loud.mml_db_sl} dB SL` : '—'],
                  ['Residual Inhibition (RI)', loud.ri_present ? `Present${loud.ri_minutes ? ` (${loud.ri_minutes} minutes)` : ''}` : 'Absent'],
                  ['Tinnitus Laterality', lat],
                  ['Tinnitus Perception Nature', s.summary.perception_nature],
                  ['Test Reliability', s.summary.test_reliability],
                ].map(([k, v], i) => (
                  <tr key={i} style={{ backgroundColor: i % 2 ? BRAND.tableAlt : '#ffffff' }}>
                    <td className="border px-2 py-0.5 font-semibold" style={{ borderColor: BRAND.border }}>{k}</td>
                    <td className="border px-2 py-0.5" style={{ borderColor: BRAND.border }}>{v}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Interpretation + Recommendations */}
      <div className="grid grid-cols-2 gap-3 mt-3" data-testid="tin-report-bottom-row">
        <div data-testid="tin-report-clinical">
          <div className="text-[11px] font-bold" style={{ color: BRAND.primary }}>Clinical Interpretation</div>
          <div className="mt-1 text-[10px]" style={{ color: '#0f172a' }}>
            {clin.split(/\n+/).filter(Boolean).map((line, i) => (
              <div key={i} className="flex gap-1"><span style={{ color: BRAND.primary }}>•</span><span>{line.replace(/^[•\-*]\s*/, '')}</span></div>
            ))}
          </div>
        </div>
        <div data-testid="tin-report-recs">
          <div className="text-[11px] font-bold" style={{ color: BRAND.primary }}>Recommendations</div>
          <div className="mt-1 text-[10px]" style={{ color: '#0f172a' }}>
            {recs.split(/\n+/).filter(Boolean).map((line, i) => (
              <div key={i} className="flex gap-1"><span style={{ color: BRAND.primary }}>•</span><span>{line.replace(/^[•\-*]\s*/, '')}</span></div>
            ))}
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="flex items-end justify-between mt-3 pt-2 border-t" style={{ borderColor: BRAND.border }} data-testid="tin-report-footer">
        <div>
          <div className="text-[11px] font-bold" style={{ color: BRAND.primary }}>{clinic?.name || 'Audinexa Hearing Clinic'}</div>
          <div className="text-[9px]" style={{ color: BRAND.textMuted }}>📍 {clinic?.address_line1}{clinic?.address_line2 ? `, ${clinic.address_line2}` : ''}</div>
          <div className="text-[9px]" style={{ color: BRAND.textMuted }}>
            {clinic?.tel && <>📞 {clinic.tel}&nbsp;&nbsp;</>}{clinic?.email && <>✉ {clinic.email}</>}
          </div>
        </div>
        <div className="text-center text-[10px] italic" style={{ color: '#334155' }}>
          <div>Technology for Today.</div>
          <div>Better Hearing for Tomorrow.</div>
        </div>
        <div className="text-right">
          <div className="border-t pt-0.5 min-w-[140px]" style={{ borderColor: '#475569' }}>
            <div className="font-bold text-[11px]">{audiologistName || 'Audiologist'}</div>
            <div className="text-[9px]" style={{ color: BRAND.textMuted }}>Audiologist</div>
            {license && <div className="text-[9px]" style={{ color: BRAND.textMuted }}>Reg. No.: {license}</div>}
          </div>
        </div>
      </div>
      {sessionId && <div className="mt-1 text-[8px] text-right" style={{ color: BRAND.textMuted }}>Ref: {sessionId}</div>}
    </div>
  );
};

export const TinnitogramReportViewWithFetch = ({ state, patient, sessionId, audiologistName }) => {
  const [clinic, setClinic] = useState({ name: '', tagline: '', address_line1: '', address_line2: '', tel: '', email: '', logo_base64: null });
  const [license, setLicense] = useState('');
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await axios.get(`${API}/settings/clinic${sessionId ? `?session_id=${sessionId}` : ''}`);
        const c = r.data || {};
        if (!alive) return;
        setClinic({
          name: c.name || '', tagline: c.tagline || '',
          address_line1: c.address || '',
          address_line2: [c.city, c.state, c.pincode].filter(Boolean).join(', '),
          tel: c.phone || '', email: c.email || '', logo_base64: null,
        });
        try {
          const lr = await axios.get(`${API}/settings/clinic/logo`, { responseType: 'blob' });
          if (alive) setClinic((prev) => ({ ...prev, logo_base64: URL.createObjectURL(lr.data) }));
        } catch { /* no logo */ }
      } catch { /* fallback */ }
      try {
        if (sessionId) {
          const sr = await axios.get(`${API}/sessions/${sessionId}`);
          const uid = sr.data?.audiologist_user_id;
          if (uid && alive) {
            const lr = await axios.get(`${API}/settings/users/${uid}/signature`, { responseType: 'blob' });
            const lic = lr?.headers?.['x-license-no'] || lr?.headers?.['X-License-No'] || '';
            if (alive) setLicense(lic);
          }
        }
      } catch { /* no license */ }
    })();
    return () => { alive = false; };
  }, [sessionId]);

  return (
    <TinnitogramReportView state={state} patient={patient} clinic={clinic} audiologistName={audiologistName} license={license} sessionId={sessionId} />
  );
};

export default TinnitogramReportView;
